import { useRef, useState } from 'react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import api from '@/utils/api';
import { toast } from 'sonner';
import { ImagePlus, Loader2, Trash2 } from 'lucide-react';
import { COURSE_ICONS, getCourseIcon } from '@/utils/courseIcons';
import { uploadImageToS3 } from '@/utils/s3Upload';
import { isTeacher } from '@/utils/auth';

const MAX_IMAGE_MB = 5;

export const CourseIconPicker = ({ courseId, value, imageUrl, onChange, onImageChange, size = 'default' }) => {
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [progress, setProgress] = useState(0);
  const fileRef = useRef(null);
  const Current = getCourseIcon(value);

  if (!isTeacher()) return null;

  const save = async (icon) => {
    setSaving(true);
    try {
      await api.patch(`/courses/${courseId}/icon`, { icon });
      onChange?.(icon);
      toast.success(icon ? 'Course icon updated' : 'Icon reset to default');
      setOpen(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to update icon');
    } finally {
      setSaving(false);
    }
  };

  const saveImage = async (url) => {
    await api.patch(`/courses/${courseId}/image`, { thumbnail_url: url });
    onImageChange?.(url);
  };

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (!file.type.startsWith('image/')) return toast.error('Please choose an image file');
    if (file.size > MAX_IMAGE_MB * 1024 * 1024) return toast.error(`Image must be under ${MAX_IMAGE_MB}MB`);
    setSaving(true);
    setProgress(0);
    try {
      const { url } = await uploadImageToS3(file, 'images/', setProgress).promise;
      await saveImage(url);
      toast.success('Course image updated');
      setOpen(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message || 'Failed to upload image');
    } finally {
      setSaving(false);
      setProgress(0);
    }
  };

  const removeImage = async () => {
    setSaving(true);
    try {
      await saveImage(null);
      toast.success('Course image removed');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to remove image');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          size={size === 'sm' ? 'sm' : 'default'}
          className="rounded-full"
          onClick={(e) => e.stopPropagation()}
          data-testid={`course-icon-picker-${courseId}`}
        >
          <Current className="mr-2 h-4 w-4" />
          {value || imageUrl ? 'Change Icon' : 'Choose Icon'}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-72" onClick={(e) => e.stopPropagation()} data-testid="course-icon-picker-panel">
        <p className="text-sm font-medium mb-2" style={{ color: '#163329' }}>Your own image</p>
        {imageUrl && (
          <div className="flex items-center gap-2 mb-2">
            <img src={imageUrl} alt="Course" className="h-12 w-16 object-cover rounded-md border" style={{ borderColor: '#E3E1DB' }} data-testid="course-image-preview" />
            <Button variant="ghost" size="sm" className="text-red-600" disabled={saving} onClick={removeImage} data-testid="course-image-remove">
              <Trash2 className="h-4 w-4 mr-1" /> Remove
            </Button>
          </div>
        )}
        <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={handleFile} data-testid="course-image-input" />
        <Button variant="outline" size="sm" className="w-full" disabled={saving} onClick={() => fileRef.current?.click()} data-testid="course-image-upload-btn">
          {saving && progress > 0 ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <ImagePlus className="h-4 w-4 mr-2" />}
          {imageUrl ? 'Replace from gallery' : 'Upload from gallery'}
        </Button>
        {saving && progress > 0 && <Progress value={progress} className="mt-2 h-1.5" />}
        <p className="text-xs mt-1 mb-3" style={{ color: '#8B9187' }}>
          JPG/PNG up to {MAX_IMAGE_MB}MB. Shown instead of the icon on course cards.
        </p>

        <p className="text-sm font-medium mb-2 pt-3 border-t" style={{ color: '#163329', borderColor: '#E3E1DB' }}>Or pick an illustration icon</p>
        <div className="grid grid-cols-6 gap-2">
          {Object.entries(COURSE_ICONS).map(([name, Icon]) => (
            <button
              key={name}
              type="button"
              disabled={saving}
              onClick={() => save(name)}
              title={name}
              className="h-10 w-10 flex items-center justify-center rounded-lg border hover:bg-[#F0EFEA] transition-colors"
              style={{ borderColor: value === name ? '#163329' : '#E3E1DB', backgroundColor: value === name ? '#E8F5E9' : undefined }}
              data-testid={`course-icon-option-${name}`}
            >
              <Icon className="h-5 w-5" style={{ color: '#163329' }} />
            </button>
          ))}
        </div>
        {imageUrl && <p className="text-xs mt-2" style={{ color: '#8B9187' }}>Remove your image to show the icon instead.</p>}
        {value && (
          <Button variant="ghost" size="sm" className="w-full mt-3" disabled={saving} onClick={() => save(null)} data-testid="course-icon-reset">
            Reset icon to default
          </Button>
        )}
      </PopoverContent>
    </Popover>
  );
};
